import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Status } from '@df/core/status';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextComponent } from '../../common/text/text.component';
import { defaultImage } from '../../icms.config';

export namespace NCmsUGC {
    export const MODAL_ID = 'ugc-modal';
    export interface IComponentData extends NICmsContentComponent.IData, NTextComponent.ITextContent {
        links: IUGC[];
    }
    export interface IUGC {
        tagName: string;
        tagLink: string;
        image: string;
        trayHeading: string;
        trayProducts: number[];
    }
    export interface IModalData {
        title: string;
        products: Product<IProductDataV2>[];
    }
}

@ICmsComponent<CmsUGCComponent>({
    id: 'ugc',
    name: 'UGC',
    tags: ['carousel', 'ugc', 'links'],
    fields: [
        ...NTextComponent.getFields(),
        // links
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'links',
            title: 'Links',
            itemName: 'Link',
            itemNameField: 'tagName',
            required: true,
            fields: [
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'tagName',
                    title: 'Tag name',
                    default: '@dragonfly',
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'tagLink',
                    title: 'Tag link',
                    default: '/',
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    default: defaultImage,
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'trayHeading',
                    title: 'Tray heading',
                    default: 'Tray heading'
                },
                <NICmsContentField.IRelationField>{
                    key: 'trayProducts',
                    type: NICmsContentField.EFieldType.relation,
                    title: 'Tray products',
                    options: {
                        title: 'Product',
                        type: 'product',
                        multiple: true,
                        qty: 10
                    },
                    multiple: true,
                    description: 'If empty we will use button as a link'
                }
            ],
            defaultSize: 7
        }
    ]
})
@Component({
    selector: 'cms-ugc',
    templateUrl: './cms-ugc.html',
    styleUrls: ['./cms-ugc.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsUGCComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NCmsUGC.IComponentData;
    protected trays = new Map<number[], Product<IProductDataV2>[]>();
    protected trayOpenStatus = new Map<NCmsUGC.IUGC, Status>();
    private productResource = inject(ProductResource);
    private router = inject(Router);
    private dfModalService = inject(DfModalService);

    get pcSlides() {
        return [1.2, 2.25, 4.3, 5.6][this.client.sizeId];
    }

    get pcSlidesCeil() {
        return Math.ceil(this.pcSlides);
    }

    get pcGutter() {
        return '16px';
    }

    async openTray(ugc: NCmsUGC.IUGC) {
        const products = this.trays.get(ugc.trayProducts);
        if (!products && ugc.trayProducts?.length) {
            const newStatus = new Status();
            newStatus.busy = true;
            this.trayOpenStatus.set(ugc, newStatus);
            this.detectChanges();
            const trayProducts = (await this.productResource.loadAllIds(ugc.trayProducts, 2, { safe: true })) as Product<IProductDataV2>[];
            this.trays.set(ugc.trayProducts, trayProducts);
            newStatus.busy = false;
        }
        if (this.trays.get(ugc.trayProducts)?.length) {
            this.dfModalService.open(undefined, NCmsUGC.MODAL_ID, undefined, {
                products: this.trays.get(ugc.trayProducts),
                title: ugc.trayHeading
            } as NCmsUGC.IModalData);
        } else {
            this.router.navigateByUrl(ugc.tagLink);
        }
    }
}
